/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["$axios"] }] */
// @ts-check

import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';

// Must match the consent checkbox copy in pages/register/index.vue: HubSpot stores
// this string verbatim as the record of what the user actually agreed to.
const CONSENT_TEXT = 'I have read and agree to the Terms of Use and Privacy Policy';

export default ({ $axios, $config }, inject) => {
    // Not setting $axios.defaults.baseURL because there are multiple

    $axios.defaults.timeout = 9000;

    /**
     * @typedef {Object} FlashProfile User data from Flash Profiles
     * @property {String} userId The Flash User ID, e.g. "c999a6de-8543-4ef8-b9ab-87bb177badcc"
     * @property {String} tenantId `es::${int|prod}`
     * @property {String} email
     * @property {Boolean} emailVerified
     * @property {String} firstName
     * @property {String} lastName
     * @property {String} phoneNumber e.g. "6175551234"
     * @property {String} createdAt e.g. "2022-10-26T21:44:58.157Z"
     *
     * @typedef {Object} FlashTokens The encoded JWT's returned for a new user
     * @property {String} accessToken
     * @property {String} idToken
     * @property {String} refreshToken
     *
     * @typedef {Object<String, String | Number | Boolean>} FlashClaims Attributes of a decoded JWT
     */

    inject('api', {
        /**
         * Create a Flash Profile for a user.
         *
         * Flash Profiles allowlists exactly the fields below and derives the tenant
         * namespace from its own environment; anything else in the body (e.g.
         * tenantId or emailVerified) is silently ignored.
         *
         * @param {Object} data The properties for the new user
         * @param {String} data.email
         * @param {String} data.firstName
         * @param {String} data.lastName
         * @param {String} data.phoneNumber
         * @returns {Promise<FlashProfile & FlashTokens>} The user data from Flash Profiles
         */
        async createUser(data) {
            // Remove empty keys from data
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([, value]) => Boolean(value)),
            );

            return $axios.$post(
                `${$config.PROFILE_DOMAIN}/users/`,
                cleanData,
                {
                    timeout: 15000,
                },
            );
        },

        /**
         * Get the Flash Profile for a user.
         * @param {Object} args
         * @param {String} args.idToken The ID token JWT from Cognito; passed explicitly to avoid coupling to $auth
         * @returns {Promise<FlashProfile>} The user data from Flash Profiles
         */
        async getUser({ idToken }) {
            // This function is called before the user is completely logged-in,
            // so decoding the ID token is the only  way to get their user ID

            const { 'custom:flash_user_id': flashUserId } = /** @type {FlashClaims} */ (jwt_decode(idToken));
            return $axios.$get(
                `${$config.PROFILE_DOMAIN}/users/${flashUserId}`,
                {
                    headers: { Authorization: `Bearer ${idToken}` },
                },
            );
        },

        /**
         * Check if a user exists, and trigger migrator if user exists on rooftop but not Cognito.
         * @param {Object} args
         * @param {String} args.username Must be in the form `${tenantId}::${email}`, likely via getTenantUsername
         * @returns {Promise<void>}
         */
        async checkUserExists({ username }) {
            await $axios.$get(`${$config.AUTH_DOMAIN}/v1/users/${username}`);
        },

        /**
         * Set a new password for a user.
         * @param {Object} args
         * @param {String} args.username Must be in the form `${tenantId}::${email}`, likely via getTenantUsername
         * @param {String} args.password The new password for the user
         * @param {String} args.idToken The ID token JWT from Cognito; passed explicitly to avoid coupling to $auth
         * @returns {Promise<void>}
         */
        async setPassword({ username, password, idToken }) {
            await $axios.$post(
                `${$config.AUTH_DOMAIN}/v1/users/${username}/set-password`,
                { password },
                {
                    headers: { Authorization: `Bearer ${idToken}` },
                },
            );
        },

        /**
         * Record the user's registration consent in HubSpot.
         *
         * Submitting to the HubSpot Form is what populates `hs_legal_basis` on the
         * contact. No-ops when HUBSPOT_FORM_URL is unset, so an environment that
         * deliberately skips HubSpot never POSTs to an empty URL.
         *
         * @param {Object} contact
         * @param {String} contact.firstName
         * @param {String} contact.lastName
         * @param {String} contact.email
         * @param {String} contact.phone E.164 formatted; may be empty
         * @param {Boolean} contact.consent Whether the user agreed to the Terms of Use and Privacy Policy
         * @returns {Promise<void>}
         */
        async submitHubspotForm({
            firstName, lastName, email, phone, consent,
        }) {
            if (!$config.HUBSPOT_FORM_URL) {
                return;
            }

            // Set by HubSpot's tracking script, which flash-ui does not currently load.
            // Optional; when present it ties the submission to the visitor's session.
            const hutk = Cookies.get('hubspotutk');

            await $axios.$post(
                $config.HUBSPOT_FORM_URL,
                {
                    fields: [
                        { name: 'firstname', value: firstName },
                        { name: 'lastname', value: lastName },
                        { name: 'email', value: email },
                        // Phone is not collected right now (TCPA), so only send one if we have it
                        ...(phone ? [{ name: 'phone', value: phone }] : []),
                    ],
                    legalConsentOptions: {
                        consent: {
                            consentToProcess: consent,
                            text: CONSENT_TEXT,
                        },
                    },
                    context: {
                        // Origin + path only: /register can carry `email` and
                        // `next` query params that don't belong in HubSpot.
                        pageUri: `${window.location.origin}${window.location.pathname}`,
                        pageName: 'Flash Accounts - Register',
                        ...(hutk ? { hutk } : {}),
                    },
                },
                { timeout: 5000 },
            );
        },

    });
};
